import { ChakraProvider, extendTheme, Box } from "@chakra-ui/react";
import type { AppProps } from "next/app";
import { RecoilRoot } from "recoil";
import VersionInfo from "../components/VersionInfo";

// 扩展主题以确保所有颜色方案可用，并优化移动端体验
const theme = extendTheme({
  config: {
    initialColorMode: "dark",
    useSystemColorMode: false,
    // 确保 SSR 兼容性
    cssVarPrefix: "chakra",
  },
  styles: {
    global: {
      html: {
        // 移动端滚动优化
        WebkitTapHighlightColor: "transparent",
        WebkitTouchCallout: "none",
        WebkitUserSelect: "none",
        touchAction: "manipulation",
      },
      body: {
        bg: "gray.900",
        color: "white",
        // 移动端字体优化
        WebkitFontSmoothing: "antialiased",
        MozOsxFontSmoothing: "grayscale",
        // 防止移动端缩放
        WebkitTextSizeAdjust: "100%",
      },
      // 移动端触摸优化
      "*": {
        WebkitTapHighlightColor: "transparent",
      },
      // 滚动条优化
      "::-webkit-scrollbar": {
        width: "8px",
        height: "8px",
      },
      "::-webkit-scrollbar-track": {
        bg: "gray.800",
      },
      "::-webkit-scrollbar-thumb": {
        bg: "gray.600",
        borderRadius: "4px",
      },
      "::-webkit-scrollbar-thumb:hover": {
        bg: "gray.500",
      },
    },
  },
  // 移动端断点优化
  breakpoints: {
    base: "0px",
    sm: "480px",
    md: "768px",
    lg: "992px",
    xl: "1280px",
    "2xl": "1536px",
  },
  // 移动端字体大小优化
  fontSizes: {
    xs: "0.75rem",
    sm: "0.875rem",
    md: "1rem",
    lg: "1.125rem",
    xl: "1.25rem",
    "2xl": "1.5rem",
    "3xl": "1.875rem",
    "4xl": "2.25rem",
    "5xl": "3rem",
    "6xl": "3.75rem",
  },
  // 移动端间距优化
  space: {
    px: "1px",
    0.5: "0.125rem",
    1: "0.25rem",
    1.5: "0.375rem",
    2: "0.5rem",
    2.5: "0.625rem",
    3: "0.75rem",
    3.5: "0.875rem",
    4: "1rem",
    5: "1.25rem",
    6: "1.5rem",
    7: "1.75rem",
    8: "2rem",
    9: "2.25rem",
    10: "2.5rem",
    12: "3rem",
    14: "3.5rem",
    16: "4rem",
    20: "5rem",
    24: "6rem",
    28: "7rem",
    32: "8rem",
    36: "9rem",
    40: "10rem",
    44: "11rem",
    48: "12rem",
    52: "13rem",
    56: "14rem",
    60: "15rem",
    64: "16rem",
    72: "18rem",
    80: "20rem",
    96: "24rem",
  },
  // 移动端组件样式优化
  components: {
    Button: {
      baseStyle: {
        // 移动端按钮触摸优化
        minH: { base: "44px", md: "40px" }, // 移动端最小触摸目标 44px
        px: { base: 4, md: 3 },
        fontSize: { base: "md", md: "sm" },
        fontWeight: "medium",
        borderRadius: "md",
        _focus: {
          boxShadow: "0 0 0 3px rgba(66, 153, 225, 0.6)",
        },
        _active: {
          transform: "scale(0.98)",
        },
      },
      sizes: {
        sm: {
          minH: { base: "36px", md: "32px" },
          fontSize: { base: "sm", md: "xs" },
          px: { base: 3, md: 2 },
        },
        md: {
          minH: { base: "44px", md: "40px" },
          fontSize: { base: "md", md: "sm" },
          px: { base: 4, md: 3 },
        },
        lg: {
          minH: { base: "52px", md: "48px" },
          fontSize: { base: "lg", md: "md" },
          px: { base: 6, md: 5 },
        },
      },
    },
    Card: {
      baseStyle: {
        container: {
          borderRadius: { base: "lg", md: "md" },
          p: { base: 4, md: 3 },
        },
      },
    },
    Heading: {
      baseStyle: {
        fontWeight: "bold",
        lineHeight: "shorter",
      },
      sizes: {
        xs: { fontSize: { base: "md", md: "sm" } },
        sm: { fontSize: { base: "lg", md: "md" } },
        md: { fontSize: { base: "xl", md: "lg" } },
        lg: { fontSize: { base: "2xl", md: "xl" } },
        xl: { fontSize: { base: "3xl", md: "2xl" } },
        "2xl": { fontSize: { base: "4xl", md: "3xl" } },
      },
    },
    Text: {
      baseStyle: {
        lineHeight: "base",
      },
    },
    Switch: {
      baseStyle: {
        track: {
          _focus: {
            boxShadow: "0 0 0 3px rgba(66, 153, 225, 0.6)",
          },
        },
      },
      sizes: {
        sm: {
          track: {
            w: { base: "1.875rem", md: "1.375rem" },
            h: { base: "1rem", md: "0.75rem" },
          },
          thumb: {
            w: { base: "0.875rem", md: "0.625rem" },
            h: { base: "0.875rem", md: "0.625rem" },
          },
        },
        md: {
          track: {
            w: { base: "2.25rem", md: "1.875rem" },
            h: { base: "1.25rem", md: "1rem" },
          },
          thumb: {
            w: { base: "1.125rem", md: "0.875rem" },
            h: { base: "1.125rem", md: "0.875rem" },
          },
        },
        lg: {
          track: {
            w: { base: "2.75rem", md: "2.25rem" },
            h: { base: "1.5rem", md: "1.25rem" },
          },
          thumb: {
            w: { base: "1.375rem", md: "1.125rem" },
            h: { base: "1.375rem", md: "1.125rem" },
          },
        },
      },
    },
  },
});

function MyApp({ Component, pageProps }: AppProps) {
  return (
    <RecoilRoot>
      <ChakraProvider theme={theme} resetCSS={true}>
        <Box position="relative" minHeight="100vh">
          <Component {...pageProps} />
          {/* 全局版本信息 - 固定在右下角 */}
          <VersionInfo variant="compact" position="fixed" />
        </Box>
      </ChakraProvider>
    </RecoilRoot>
  );
}

export default MyApp;
